/**
 * /intercompany/transactions — dedicated transaction entry page.
 *
 * Tabbed interface with one tab per transaction type:
 *   1. Paid on behalf of
 *   2. Cash transfer
 *   3. Capital injection   (admin only)
 *   4. Distribution        (admin only)
 *   5. Shared labor
 *
 * Each tab contains a form whose fields exactly match the backend
 * Create schema.  On success the IC query cache is invalidated.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  entitiesListQueryOptions,
  icKeys,
  meQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type { Entity } from "@/lib/types";

export const Route = createFileRoute("/_auth/intercompany/transactions")({
  component: IntercompanyTransactionsPage,
});

// ──────────────────────────────────────────────────────────────
// Tab definitions (order matches backend docs)
// ──────────────────────────────────────────────────────────────
type TxTab =
  | "paid_on_behalf_of"
  | "cash_transfer"
  | "capital_injection"
  | "distribution"
  | "shared_labor";

interface TabDef {
  value: TxTab;
  label: string;
  adminOnly: boolean;
}

const TABS: TabDef[] = [
  { value: "paid_on_behalf_of",  label: "Paid on behalf of", adminOnly: false },
  { value: "cash_transfer",      label: "Cash transfer",      adminOnly: false },
  { value: "capital_injection",  label: "Capital injection",  adminOnly: true  },
  { value: "distribution",       label: "Distribution",       adminOnly: true  },
  { value: "shared_labor",       label: "Shared labor",       adminOnly: false },
];

const ENDPOINTS: Record<TxTab, string> = {
  paid_on_behalf_of: "/api/intercompany/transactions/paid-on-behalf-of",
  cash_transfer:     "/api/intercompany/transactions/cash-transfer",
  capital_injection: "/api/intercompany/transactions/capital-injection",
  distribution:      "/api/intercompany/transactions/distribution",
  shared_labor:      "/api/intercompany/transactions/shared-labor",
};

// ──────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────
function IntercompanyTransactionsPage() {
  const [tab, setTab] = useState<TxTab>("paid_on_behalf_of");
  const meQuery  = useQuery(meQueryOptions());
  const isAdmin  = meQuery.data?.role === "admin";
  const visibleTabs = isAdmin ? TABS : TABS.filter((t) => !t.adminOnly);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <Link
            to="/intercompany"
            className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Intercompany
          </Link>
          <span className="text-slate-400 dark:text-slate-600">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            New transaction
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TxTab)}
          sx={{ minHeight: 40 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={t.label}
              sx={{ minHeight: 40, fontSize: "0.8125rem" }}
            />
          ))}
        </Tabs>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
        <div className="max-w-xl">
          {tab === "paid_on_behalf_of" && <PaidOnBehalfOfForm />}
          {tab === "cash_transfer"     && <CashTransferForm />}
          {tab === "capital_injection" && <CapitalInjectionForm />}
          {tab === "distribution"      && <DistributionForm />}
          {tab === "shared_labor"      && <SharedLaborForm />}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Shared helpers
// ──────────────────────────────────────────────────────────────
function EntitySelect({
  label,
  value,
  onChange,
  entities,
  excludeId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  entities: Entity[];
  excludeId?: string;
}) {
  const opts = excludeId ? entities.filter((e) => e.id !== excludeId) : entities;
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
    >
      {opts.map((e) => (
        <MenuItem key={e.id} value={e.id}>
          {e.code} — {e.display_name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function AmountField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      size="small"
      label="Amount"
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ step: "0.01", min: "0.01" }}
      fullWidth
    />
  );
}

function DescriptionField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      size="small"
      label="Description"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline
      minRows={2}
      fullWidth
    />
  );
}

function DateField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      size="small"
      label="Journal date (optional — defaults to today)"
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
  );
}

function CurrencyField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      size="small"
      label="Currency code"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ maxLength: 3 }}
      helperText="ISO 4217 — leave USD unless multi-currency"
      fullWidth
    />
  );
}

interface SubmitResult {
  id: string;
  transaction_type: string;
  amount: string | number;
  initiator_code?: string | null;
  counterparty_code?: string | null;
}

function SuccessBanner({ tx, onClear }: { tx: SubmitResult; onClear: () => void }) {
  return (
    <Alert
      severity="success"
      onClose={onClear}
      sx={{ mb: 2 }}
    >
      <strong>Posted.</strong> {tx.transaction_type.replace(/_/g, " ")}{" "}
      {tx.initiator_code && tx.counterparty_code
        ? `(${tx.initiator_code} → ${tx.counterparty_code})`
        : null}{" "}
      for{" "}
      {Number(tx.amount).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      })}{" "}
      — transaction ID: <code className="text-xs">{tx.id}</code>
    </Alert>
  );
}

function FormShell({
  title,
  description,
  onSubmit,
  disabled,
  isPending,
  children,
  lastResult,
  onClear,
}: {
  title: string;
  description: string;
  onSubmit: () => void;
  disabled: boolean;
  isPending: boolean;
  children: React.ReactNode;
  lastResult: SubmitResult | null;
  onClear: () => void;
}) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontSize: "1rem", fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {lastResult && <SuccessBanner tx={lastResult} onClear={onClear} />}

      <Stack spacing={2}>
        {children}
        <Button
          variant="contained"
          disabled={disabled}
          onClick={onSubmit}
        >
          {isPending ? "Posting…" : "Post transaction"}
        </Button>
      </Stack>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// 1. Paid on behalf of
//    payer_entity_id, owner_entity_id, amount,
//    payer_cash_account_code (def 1110),
//    owner_expense_account_code, description,
//    journal_date?, currency_code (def USD)
// ──────────────────────────────────────────────────────────────
function PaidOnBehalfOfForm() {
  const qc    = useQueryClient();
  const toast = useToast();
  const { data: entities = [] } = useQuery(entitiesListQueryOptions());

  const [payerEntityId,            setPayerEntityId]            = useState("");
  const [ownerEntityId,            setOwnerEntityId]            = useState("");
  const [amount,                   setAmount]                   = useState("");
  const [payerCashAccountCode,     setPayerCashAccountCode]     = useState("1110");
  const [ownerExpenseAccountCode,  setOwnerExpenseAccountCode]  = useState("5100");
  const [description,              setDescription]              = useState("");
  const [journalDate,              setJournalDate]              = useState("");
  const [currencyCode,             setCurrencyCode]             = useState("USD");
  const [lastResult,               setLastResult]               = useState<SubmitResult | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<SubmitResult>(ENDPOINTS.paid_on_behalf_of, {
        method: "POST",
        body: {
          payer_entity_id:           payerEntityId,
          owner_entity_id:           ownerEntityId,
          amount,
          payer_cash_account_code:   payerCashAccountCode,
          owner_expense_account_code: ownerExpenseAccountCode,
          description,
          ...(journalDate ? { journal_date: journalDate } : {}),
          currency_code: currencyCode,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Paid-on-behalf-of transaction posted");
      setLastResult(data);
      setAmount(""); setDescription(""); setJournalDate("");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const ready = Boolean(payerEntityId && ownerEntityId && amount && ownerExpenseAccountCode && description);

  return (
    <FormShell
      title="Paid on behalf of"
      description="Entity A (payer) pays a vendor invoice that legally belongs to Entity B (owner). Creates Due-from on payer and an expense + Due-to on owner."
      onSubmit={() => submit.mutate()}
      disabled={!ready || submit.isPending}
      isPending={submit.isPending}
      lastResult={lastResult}
      onClear={() => setLastResult(null)}
    >
      <EntitySelect label="Payer entity (who paid cash)"   value={payerEntityId}  onChange={setPayerEntityId}  entities={entities} />
      <EntitySelect label="Owner entity (whose bill this is)" value={ownerEntityId} onChange={setOwnerEntityId} entities={entities} excludeId={payerEntityId} />
      <AmountField value={amount} onChange={setAmount} />
      <TextField size="small" label="Payer cash account code" value={payerCashAccountCode}
                 onChange={(e) => setPayerCashAccountCode(e.target.value)}
                 helperText="Cash account code on the payer's CoA (default 1110)" fullWidth />
      <TextField size="small" label="Owner expense account code" value={ownerExpenseAccountCode}
                 onChange={(e) => setOwnerExpenseAccountCode(e.target.value)}
                 helperText="Expense account code on the OWNER's CoA (e.g. 5100)" fullWidth />
      <DescriptionField value={description} onChange={setDescription} />
      <DateField value={journalDate} onChange={setJournalDate} />
      <CurrencyField value={currencyCode} onChange={setCurrencyCode} />
    </FormShell>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Cash transfer
//    from_entity_id, to_entity_id, amount,
//    from_cash_account_code (def 1110),
//    to_cash_account_code (def 1110),
//    description, journal_date?, currency_code
// ──────────────────────────────────────────────────────────────
function CashTransferForm() {
  const qc    = useQueryClient();
  const toast = useToast();
  const { data: entities = [] } = useQuery(entitiesListQueryOptions());

  const [fromEntityId,         setFromEntityId]         = useState("");
  const [toEntityId,           setToEntityId]           = useState("");
  const [amount,               setAmount]               = useState("");
  const [fromCashAccountCode,  setFromCashAccountCode]  = useState("1110");
  const [toCashAccountCode,    setToCashAccountCode]    = useState("1110");
  const [description,          setDescription]          = useState("");
  const [journalDate,          setJournalDate]          = useState("");
  const [currencyCode,         setCurrencyCode]         = useState("USD");
  const [lastResult,           setLastResult]           = useState<SubmitResult | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<SubmitResult>(ENDPOINTS.cash_transfer, {
        method: "POST",
        body: {
          from_entity_id:        fromEntityId,
          to_entity_id:          toEntityId,
          amount,
          from_cash_account_code: fromCashAccountCode,
          to_cash_account_code:   toCashAccountCode,
          description,
          ...(journalDate ? { journal_date: journalDate } : {}),
          currency_code: currencyCode,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Cash transfer posted");
      setLastResult(data);
      setAmount(""); setDescription(""); setJournalDate("");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const ready = Boolean(fromEntityId && toEntityId && amount && description);

  return (
    <FormShell
      title="Cash transfer"
      description="Moves cash between two entities that are NOT in a parent/sub relationship. Uses intercompany Due-from/Due-to to balance both ledgers."
      onSubmit={() => submit.mutate()}
      disabled={!ready || submit.isPending}
      isPending={submit.isPending}
      lastResult={lastResult}
      onClear={() => setLastResult(null)}
    >
      <EntitySelect label="From entity" value={fromEntityId} onChange={setFromEntityId} entities={entities} />
      <EntitySelect label="To entity"   value={toEntityId}   onChange={setToEntityId}   entities={entities} excludeId={fromEntityId} />
      <AmountField value={amount} onChange={setAmount} />
      <TextField size="small" label="From cash account code" value={fromCashAccountCode}
                 onChange={(e) => setFromCashAccountCode(e.target.value)}
                 helperText="Cash account code on the FROM entity's CoA (default 1110)" fullWidth />
      <TextField size="small" label="To cash account code" value={toCashAccountCode}
                 onChange={(e) => setToCashAccountCode(e.target.value)}
                 helperText="Cash account code on the TO entity's CoA (default 1110)" fullWidth />
      <DescriptionField value={description} onChange={setDescription} />
      <DateField value={journalDate} onChange={setJournalDate} />
      <CurrencyField value={currencyCode} onChange={setCurrencyCode} />
    </FormShell>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Capital injection  (admin only)
//    parent_entity_id, sub_entity_id, amount,
//    parent_cash_account_code (def 1110),
//    sub_cash_account_code (def 1110),
//    parent_investment_account_code (def 1900),
//    sub_equity_account_code (def 3050),
//    description, journal_date?, currency_code
// ──────────────────────────────────────────────────────────────
function CapitalInjectionForm() {
  const qc    = useQueryClient();
  const toast = useToast();
  const { data: entities = [] } = useQuery(entitiesListQueryOptions());

  const [parentEntityId,              setParentEntityId]              = useState("");
  const [subEntityId,                 setSubEntityId]                 = useState("");
  const [amount,                      setAmount]                      = useState("");
  const [parentCashAccountCode,       setParentCashAccountCode]       = useState("1110");
  const [subCashAccountCode,          setSubCashAccountCode]          = useState("1110");
  const [parentInvestmentAccountCode, setParentInvestmentAccountCode] = useState("1900");
  const [subEquityAccountCode,        setSubEquityAccountCode]        = useState("3050");
  const [description,                 setDescription]                 = useState("");
  const [journalDate,                 setJournalDate]                 = useState("");
  const [currencyCode,                setCurrencyCode]                = useState("USD");
  const [lastResult,                  setLastResult]                  = useState<SubmitResult | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<SubmitResult>(ENDPOINTS.capital_injection, {
        method: "POST",
        body: {
          parent_entity_id:               parentEntityId,
          sub_entity_id:                  subEntityId,
          amount,
          parent_cash_account_code:       parentCashAccountCode,
          sub_cash_account_code:          subCashAccountCode,
          parent_investment_account_code: parentInvestmentAccountCode,
          sub_equity_account_code:        subEquityAccountCode,
          description,
          ...(journalDate ? { journal_date: journalDate } : {}),
          currency_code: currencyCode,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Capital injection posted");
      setLastResult(data);
      setAmount(""); setDescription(""); setJournalDate("");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const ready = Boolean(parentEntityId && subEntityId && amount && description);

  return (
    <FormShell
      title="Capital injection"
      description="Parent entity funds a subsidiary. DR Parent cash out / Investment-in-Sub asset; CR Sub cash in / Member Contributions equity."
      onSubmit={() => submit.mutate()}
      disabled={!ready || submit.isPending}
      isPending={submit.isPending}
      lastResult={lastResult}
      onClear={() => setLastResult(null)}
    >
      <EntitySelect label="Parent entity" value={parentEntityId} onChange={setParentEntityId} entities={entities} />
      <EntitySelect label="Sub entity"    value={subEntityId}    onChange={setSubEntityId}    entities={entities} excludeId={parentEntityId} />
      <AmountField value={amount} onChange={setAmount} />
      <TextField size="small" label="Parent cash account code" value={parentCashAccountCode}
                 onChange={(e) => setParentCashAccountCode(e.target.value)}
                 helperText="Parent's cash account being debited (default 1110)" fullWidth />
      <TextField size="small" label="Sub cash account code" value={subCashAccountCode}
                 onChange={(e) => setSubCashAccountCode(e.target.value)}
                 helperText="Sub's cash account being credited (default 1110)" fullWidth />
      <TextField size="small" label="Parent investment account code" value={parentInvestmentAccountCode}
                 onChange={(e) => setParentInvestmentAccountCode(e.target.value)}
                 helperText="Investment-in-Subsidiary asset on parent (default 1900)" fullWidth />
      <TextField size="small" label="Sub equity account code" value={subEquityAccountCode}
                 onChange={(e) => setSubEquityAccountCode(e.target.value)}
                 helperText="Member-contributions equity on sub (default 3050)" fullWidth />
      <DescriptionField value={description} onChange={setDescription} />
      <DateField value={journalDate} onChange={setJournalDate} />
      <CurrencyField value={currencyCode} onChange={setCurrencyCode} />
    </FormShell>
  );
}

// ──────────────────────────────────────────────────────────────
// 4. Distribution  (admin only)
//    sub_entity_id, parent_entity_id, amount,
//    sub_cash_account_code (def 1110),
//    parent_cash_account_code (def 1110),
//    sub_distribution_account_code (def 3060),
//    parent_distribution_received_account_code (def 1900),
//    description, journal_date?, currency_code
// ──────────────────────────────────────────────────────────────
function DistributionForm() {
  const qc    = useQueryClient();
  const toast = useToast();
  const { data: entities = [] } = useQuery(entitiesListQueryOptions());

  const [subEntityId,                               setSubEntityId]                               = useState("");
  const [parentEntityId,                            setParentEntityId]                            = useState("");
  const [amount,                                    setAmount]                                    = useState("");
  const [subCashAccountCode,                        setSubCashAccountCode]                        = useState("1110");
  const [parentCashAccountCode,                     setParentCashAccountCode]                     = useState("1110");
  const [subDistributionAccountCode,                setSubDistributionAccountCode]                = useState("3060");
  const [parentDistributionReceivedAccountCode,     setParentDistributionReceivedAccountCode]     = useState("1900");
  const [description,                               setDescription]                               = useState("");
  const [journalDate,                               setJournalDate]                               = useState("");
  const [currencyCode,                              setCurrencyCode]                              = useState("USD");
  const [lastResult,                                setLastResult]                                = useState<SubmitResult | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<SubmitResult>(ENDPOINTS.distribution, {
        method: "POST",
        body: {
          sub_entity_id:                                subEntityId,
          parent_entity_id:                             parentEntityId,
          amount,
          sub_cash_account_code:                        subCashAccountCode,
          parent_cash_account_code:                     parentCashAccountCode,
          sub_distribution_account_code:                subDistributionAccountCode,
          parent_distribution_received_account_code:    parentDistributionReceivedAccountCode,
          description,
          ...(journalDate ? { journal_date: journalDate } : {}),
          currency_code: currencyCode,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Distribution posted");
      setLastResult(data);
      setAmount(""); setDescription(""); setJournalDate("");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const ready = Boolean(subEntityId && parentEntityId && amount && description);

  return (
    <FormShell
      title="Distribution"
      description="Sub entity pays a cash distribution to its parent. DR Sub distribution equity / Due-to-Parent; CR Sub cash / Parent cash + distribution received."
      onSubmit={() => submit.mutate()}
      disabled={!ready || submit.isPending}
      isPending={submit.isPending}
      lastResult={lastResult}
      onClear={() => setLastResult(null)}
    >
      <EntitySelect label="Sub entity (paying the distribution)" value={subEntityId}    onChange={setSubEntityId}    entities={entities} />
      <EntitySelect label="Parent entity (receiving cash)"       value={parentEntityId} onChange={setParentEntityId} entities={entities} excludeId={subEntityId} />
      <AmountField value={amount} onChange={setAmount} />
      <TextField size="small" label="Sub cash account code" value={subCashAccountCode}
                 onChange={(e) => setSubCashAccountCode(e.target.value)}
                 helperText="Sub's cash account (default 1110)" fullWidth />
      <TextField size="small" label="Parent cash account code" value={parentCashAccountCode}
                 onChange={(e) => setParentCashAccountCode(e.target.value)}
                 helperText="Parent's cash account receiving the distribution (default 1110)" fullWidth />
      <TextField size="small" label="Sub distribution account code" value={subDistributionAccountCode}
                 onChange={(e) => setSubDistributionAccountCode(e.target.value)}
                 helperText="Distribution equity contra on sub (default 3060)" fullWidth />
      <TextField size="small" label="Parent distribution received account code" value={parentDistributionReceivedAccountCode}
                 onChange={(e) => setParentDistributionReceivedAccountCode(e.target.value)}
                 helperText="Distribution-received account on parent (default 1900)" fullWidth />
      <DescriptionField value={description} onChange={setDescription} />
      <DateField value={journalDate} onChange={setJournalDate} />
      <CurrencyField value={currencyCode} onChange={setCurrencyCode} />
    </FormShell>
  );
}

// ──────────────────────────────────────────────────────────────
// 5. Shared labor
//    employer_entity_id, benefiting_entity_id, amount,
//    employer_labor_contra_account_code (def 5290),
//    benefiting_labor_account_code (def 5200),
//    description, journal_date?, currency_code
// ──────────────────────────────────────────────────────────────
function SharedLaborForm() {
  const qc    = useQueryClient();
  const toast = useToast();
  const { data: entities = [] } = useQuery(entitiesListQueryOptions());

  const [employerEntityId,               setEmployerEntityId]               = useState("");
  const [benefitingEntityId,             setBenefitingEntityId]             = useState("");
  const [amount,                         setAmount]                         = useState("");
  const [employerLaborContraAccountCode, setEmployerLaborContraAccountCode] = useState("5290");
  const [benefitingLaborAccountCode,     setBenefitingLaborAccountCode]     = useState("5200");
  const [description,                    setDescription]                    = useState("");
  const [journalDate,                    setJournalDate]                    = useState("");
  const [currencyCode,                   setCurrencyCode]                   = useState("USD");
  const [lastResult,                     setLastResult]                     = useState<SubmitResult | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<SubmitResult>(ENDPOINTS.shared_labor, {
        method: "POST",
        body: {
          employer_entity_id:                employerEntityId,
          benefiting_entity_id:              benefitingEntityId,
          amount,
          employer_labor_contra_account_code: employerLaborContraAccountCode,
          benefiting_labor_account_code:      benefitingLaborAccountCode,
          description,
          ...(journalDate ? { journal_date: journalDate } : {}),
          currency_code: currencyCode,
        },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Shared labor transaction posted");
      setLastResult(data);
      setAmount(""); setDescription(""); setJournalDate("");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const ready = Boolean(employerEntityId && benefitingEntityId && amount && description);

  return (
    <FormShell
      title="Shared labor"
      description="An employee on Entity A's payroll works on Entity B's project. Removes the cost from A's labor expense and posts it as a labor expense on B, with intercompany balancing entries."
      onSubmit={() => submit.mutate()}
      disabled={!ready || submit.isPending}
      isPending={submit.isPending}
      lastResult={lastResult}
      onClear={() => setLastResult(null)}
    >
      <EntitySelect label="Employer entity (carries payroll)"    value={employerEntityId}   onChange={setEmployerEntityId}   entities={entities} />
      <EntitySelect label="Benefiting entity (received the work)" value={benefitingEntityId} onChange={setBenefitingEntityId} entities={entities} excludeId={employerEntityId} />
      <AmountField value={amount} onChange={setAmount} />
      <TextField size="small" label="Employer labor contra account code" value={employerLaborContraAccountCode}
                 onChange={(e) => setEmployerLaborContraAccountCode(e.target.value)}
                 helperText="Labor contra on employer to net out the cost (default 5290)" fullWidth />
      <TextField size="small" label="Benefiting labor account code" value={benefitingLaborAccountCode}
                 onChange={(e) => setBenefitingLaborAccountCode(e.target.value)}
                 helperText="Labor expense account on the benefiting entity (default 5200)" fullWidth />
      <DescriptionField value={description} onChange={setDescription} />
      <DateField value={journalDate} onChange={setJournalDate} />
      <CurrencyField value={currencyCode} onChange={setCurrencyCode} />
    </FormShell>
  );
}
